import { useEffect, useRef, useState } from 'react';
import { api, type BattleRoom } from '../lib/api';
import type { ViewId } from '../state';

type Props = {
    battleId: number;
    myId?: number | null;
    onNavigate: (id: ViewId) => void;
    onAfterResolve?: () => void;
    botUsername?: string;
};

/**
 * Sit-in lobby for a single battle — implements the "после создания попасть
 * и сидеть в этой комнате, дожидаясь анимации игры" requirement from ТЗ.
 *
 * Polls /api/battles/:id every 2.5s while the room is open. When the room
 * fills and settles server-side, we flip into a brief "rolling" animation,
 * then show the winner card. The animation is purely cosmetic — the random
 * pick already happened on the backend the moment slots filled.
 */
export function BattleLobbyView({ battleId, myId, onNavigate, onAfterResolve, botUsername }: Props) {
    const [room, setRoom] = useState<BattleRoom | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [rolling, setRolling] = useState(false);
    const [rollingFace, setRollingFace] = useState('🤪');
    const resolvedOnce = useRef(false);

    useEffect(() => {
        let stop = false;
        const tick = async () => {
            try {
                const r = await api.getBattle(battleId);
                if (stop) return;
                setRoom(r);
                if (r.status === 'finished' && !resolvedOnce.current) {
                    resolvedOnce.current = true;
                    setRolling(true);
                    onAfterResolve?.();
                    // Cycle through emoji rapidly for ~1.6s to suggest a
                    // "dice roll" before settling on the winner reveal.
                    let i = 0;
                    const faces = r.participants.map((p) => p.emoji);
                    const id = window.setInterval(() => {
                        setRollingFace(faces[i % faces.length] ?? '🎲');
                        i += 1;
                    }, 90);
                    window.setTimeout(() => {
                        window.clearInterval(id);
                        setRolling(false);
                    }, 1600);
                }
            } catch (e) {
                setError(String(e));
            }
        };
        void tick();
        const id = window.setInterval(tick, 2500);
        return () => { stop = true; window.clearInterval(id); };
    }, [battleId, onAfterResolve]);

    const share = async () => {
        const wa = window.Telegram?.WebApp;
        // Bot API 8.0 path — sends a prepared message with a real inline
        // join-button. Falls back to t.me/share/url if the client is older or
        // the backend can't prepare the message.
        if (wa?.shareMessage) {
            try {
                const r = await api.prepareBattleShare(battleId);
                wa.shareMessage(r.preparedMessageId);
                return;
            } catch { /* fall back */ }
        }
        const link = `https://t.me/${botUsername ?? 'BogStarsBot'}?startapp=battle_${battleId}`;
        const joined = room?.joined ?? 0;
        const max = room?.maxSlots ?? 0;
        const need = Math.max(0, max - joined);
        const text =
            `⚔️ Зашёл в BOG BATTLE #${battleId}!\n` +
            `Ставка: ${room?.stake ?? '?'} BSP · Игроков: ${joined}/${max}` +
            (need > 0 ? ` (нужно ещё ${need})` : '') +
            `\nЗалетай 👇`;
        const url = `https://t.me/share/url?url=${encodeURIComponent(link)}` +
                    `&text=${encodeURIComponent(text)}`;
        if (wa?.openTelegramLink) wa.openTelegramLink(url);
        else window.open(url, '_blank', 'noopener,noreferrer');
    };

    if (error && !room) {
        return (
            <div className="view view-center">
                <p className="popup-text" style={{ color: 'var(--red)' }}>{error}</p>
                <button type="button" className="back-btn" onClick={() => onNavigate('bogBattle')}>
                    К СПИСКУ
                </button>
            </div>
        );
    }
    if (!room) {
        return (
            <div className="view view-center">
                <p className="popup-text">Загружаем комнату…</p>
            </div>
        );
    }

    const won = room.status === 'finished' && room.winnerId != null && room.winnerId === myId;
    const lost = room.status === 'finished' && room.winnerId != null && room.winnerId !== myId;
    const winner = room.participants.find((p) => p.userId === room.winnerId) ?? null;

    return (
        <div className="view">
            <h2 className="text-center fw-900 text-red" style={{ fontSize: 18, letterSpacing: 2 }}>
                БАТЛ #{room.id}
            </h2>

            <div className="info-card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="text-muted f-12">Ставка</span>
                    <span className="fw-900">{room.stake} BSP</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="text-muted f-12">Банк</span>
                    <span className="fw-900" style={{ color: 'var(--accent)' }}>{room.pot} BSP</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="text-muted f-12">Слоты</span>
                    <span className="fw-900">{room.joined}/{room.maxSlots}</span>
                </div>
                {room.vip && (
                    <div style={{ textAlign: 'center', color: 'var(--accent)', fontWeight: 900, marginTop: 4 }}>
                        ⭐ VIP БАТЛ
                    </div>
                )}
            </div>

            <h3 className="text-muted fw-900 f-12" style={{ textTransform: 'uppercase' }}>
                Участники
            </h3>
            <div className="info-card" style={{ padding: 10 }}>
                <div className="b-roster" style={{ flexWrap: 'wrap' }}>
                    {room.participants.map((p) => (
                        <span
                            key={p.userId}
                            className={`b-roster-slot${p.anon ? ' anon' : ''}${p.isCreator ? ' creator' : ''}`}
                            title={p.anon ? 'Аноним' : (p.username ? '@' + p.username : (p.firstName ?? `id${p.userId}`))}
                        >
                            {p.anon ? p.emoji : (p.username ? '@' + p.username.slice(0, 8) : p.emoji)}
                        </span>
                    ))}
                    {Array.from({ length: Math.max(0, room.maxSlots - room.participants.length) }).map((_, i) => (
                        <span key={`empty-${i}`} className="b-roster-slot empty">?</span>
                    ))}
                </div>
            </div>

            {room.status === 'open' && (
                <div className="info-card" style={{ textAlign: 'center' }}>
                    <p className="popup-text">
                        Ждём ещё {room.maxSlots - room.joined} участников…
                    </p>
                    <button
                        type="button"
                        className="action-btn btn-outline-gold"
                        onClick={share}
                        style={{ marginTop: 8 }}
                    >
                        ↗ ПРИГЛАСИТЬ ДРУГА
                    </button>
                </div>
            )}

            {rolling && (
                <div className="info-card battle-roll-card" style={{ textAlign: 'center', padding: 24 }}>
                    <div style={{ fontSize: 56, lineHeight: 1 }} className="battle-roll-face">
                        {rollingFace}
                    </div>
                    <p className="popup-text" style={{ marginTop: 8 }}>Крутим барабан…</p>
                </div>
            )}

            {room.status === 'finished' && !rolling && (
                <div
                    className="info-card"
                    style={{
                        textAlign: 'center',
                        borderColor: won ? 'var(--green)' : 'var(--red)',
                        padding: 14,
                    }}
                >
                    <div style={{ fontSize: 48 }}>{won ? '🏆' : (lost ? '💀' : '🤝')}</div>
                    <p className="popup-text fw-900" style={{
                        color: won ? 'var(--green)' : 'var(--red)',
                        fontSize: 16, marginTop: 4,
                    }}>
                        {won ? `Победа! +${room.payout ?? 0} BSP`
                             : lost ? `Победил ${winner?.anon
                                          ? winner.emoji
                                          : (winner?.username ? '@' + winner.username : (winner?.firstName ?? 'игрок'))}`
                                    : 'Батл завершён.'}
                    </p>
                </div>
            )}

            <button type="button" className="back-btn" onClick={() => onNavigate('bogBattle')}>
                К СПИСКУ
            </button>
        </div>
    );
}
